import React, { useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Loader, Check, X, ZoomIn, ZoomOut } from 'lucide-react';
import { uploadFile, getCroppedBlob } from './ImageUploader';

const CROP_PRESETS = [
  { label: 'Original', ratio: null },
  { label: 'Square', ratio: 1 },
  { label: 'Landscape', ratio: 4 / 3 },
  { label: 'Portrait', ratio: 3 / 4 },
  { label: 'Wide', ratio: 16 / 9 },
];

// Freeform re-crop of an already-uploaded photo: pick a frame with
// react-easy-crop, then re-upload the cropped pixels as a new image and
// hand back its URL — a src swap, not a destructive edit of the original.
function CropModal({ src, initialRatio = null, onCancel, onConfirm }) {
  const [cropArea, setCropArea] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [ratio, setRatio] = useState(initialRatio);
  const [naturalRatio, setNaturalRatio] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const img = new window.Image();
    img.onload = () => setNaturalRatio(img.naturalWidth / img.naturalHeight || 1);
    img.src = src;
  }, [src]);

  const confirm = async () => {
    if (!pixels) return;
    setSaving(true);
    setError(null);
    try {
      const blob = await getCroppedBlob(src, pixels);
      const url = await uploadFile(new File([blob], 'cropped.jpg', { type: 'image/jpeg' }));
      onConfirm(url);
    } catch (err) {
      console.error('Crop failed:', err);
      setError('Could not crop that photo. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(32,34,38,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div style={{ background: 'white', borderRadius: 16, padding: 16, width: 480, maxWidth: '100%' }}>
        <div style={{ position: 'relative', width: '100%', height: 320, borderRadius: 12, overflow: 'hidden', background: '#20242b' }}>
          <Cropper
            image={src}
            crop={cropArea}
            zoom={zoom}
            aspect={ratio || naturalRatio}
            onCropChange={setCropArea}
            onZoomChange={setZoom}
            onCropComplete={(_, pixelCrop) => setPixels(pixelCrop)}
          />
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {CROP_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => setRatio(preset.ratio)}
              style={{
                padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                border: '1px solid #E8EBEA',
                background: ratio === preset.ratio ? '#FDE3D7' : 'white',
                color: ratio === preset.ratio ? '#C85A2E' : '#5C7383',
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <ZoomOut size={15} color="#5C7383" />
          <input
            type="range" min={1} max={3} step={0.05} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ flex: 1 }}
          />
          <ZoomIn size={15} color="#5C7383" />
        </div>
        {error && <p style={{ color: '#E5484D', fontSize: 13, marginTop: 8 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: '1px solid #E8EBEA', background: 'white', color: '#3A4047', fontSize: 14, cursor: 'pointer' }}
          >
            <X size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={saving || !pixels}
            style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: 'none', background: '#fb7f67', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? <Loader size={14} className="animate-spin" style={{ verticalAlign: -2, marginRight: 4 }} /> : <Check size={14} style={{ verticalAlign: -2, marginRight: 4 }} />}
            {saving ? 'Cropping…' : 'Crop photo'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CropModal;
